import { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, List, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';
import useParentData from '@/hooks/useParentData';
import ChildSelector from './ChildSelector';
import CalendarView from './CalendarView';

const AttendanceView = () => {
  const { children, selectedChild, setSelectedChild, loading } = useParentData();
  const [viewMode, setViewMode] = useState('calendar');
  const [attendanceData, setAttendanceData] = useState([]);
  const [stats, setStats] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (selectedChild) {
      fetchAttendance(selectedChild.id);
    }
  }, [selectedChild]);

  const fetchAttendance = async (studentId) => {
    setLoadingData(true);
    try {
      const response = await apiClient.get(`/v1/parent/child/${studentId}/attendance`);
      setAttendanceData(response.data.attendance || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to load attendance:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoadingData(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      present: 'bg-green-500',
      absent: 'bg-red-500',
      'half-day': 'bg-yellow-500',
      holiday: 'bg-blue-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusBadge = (status) => {
    const badges = {
      present: 'bg-green-100 text-green-800',
      absent: 'bg-red-100 text-red-800',
      'half-day': 'bg-yellow-100 text-yellow-800',
      holiday: 'bg-blue-100 text-blue-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>Attendance</h1>
        <p className="text-gray-600 mt-1">Track your child's attendance records</p>
      </div>

      <ChildSelector 
        children={children}
        selectedChild={selectedChild}
        onSelectChild={setSelectedChild}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <TrendingUp className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>
                {stats.percentage || 0}%
              </p>
              <p className="text-sm text-gray-600 mt-1">Attendance Rate</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{stats.present || 0}</div>
              <p className="text-sm text-gray-600 mt-1">Present Days</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">{stats.absent || 0}</div>
              <p className="text-sm text-gray-600 mt-1">Absent Days</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-600">{stats.total || 0}</div>
              <p className="text-sm text-gray-600 mt-1">Total Days</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button
          variant={viewMode === 'calendar' ? 'default' : 'outline'}
          onClick={() => setViewMode('calendar')}
          className={viewMode === 'calendar' ? 'btn-primary text-white' : ''}
        >
          <CalendarIcon className="w-4 h-4 mr-2" />
          Calendar View
        </Button>
        <Button
          variant={viewMode === 'list' ? 'default' : 'outline'}
          onClick={() => setViewMode('list')}
          className={viewMode === 'list' ? 'btn-primary text-white' : ''}
        >
          <List className="w-4 h-4 mr-2" />
          List View
        </Button>
      </div>

      {/* Content */}
      {loadingData ? (
        <div className="text-center py-12 text-gray-500">Loading attendance data...</div>
      ) : viewMode === 'calendar' ? (
        <CalendarView
          data={attendanceData}
          renderCell={(dayData) => (
            <div className={`text-xs px-2 py-1 rounded-full text-white text-center capitalize ${getStatusColor(dayData.status)}`}>
              {dayData.status}
            </div>
          )}
        />
      ) : (
        <div className="space-y-3">
          {attendanceData.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                No attendance records found
              </CardContent>
            </Card>
          ) : (
            attendanceData.map((record, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{formatDate(record.date)}</p>
                      <p className="text-sm text-gray-500">
                        Marked by {record.marked_by_role === 'admin' ? 'admin' : 'warden'}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusBadge(record.status)}`}>
                      {record.status}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AttendanceView;
